import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { stripHtmlTags } from "../../utils/StringTool";

interface FormSummaryModalProps {
  open: boolean;
  onClose: () => void;
  schema: any;
  submission: any;
}

export const FormSummaryModal: React.FC<FormSummaryModalProps> = ({
  open,
  onClose,
  schema,
  submission,
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [summaryList, setSummaryList] = useState<
    { label: string; value: string }[]
  >([]);

  function getAllFormioFields(components: any[], fields: any[] = []) {
    components.forEach((comp) => {
      if (comp.input && comp.key && comp.label) {
        fields.push({ key: comp.key, label: comp.label, type: comp.type });
      }
      if (comp.components) getAllFormioFields(comp.components, fields);
      if (comp.columns) {
        comp.columns.forEach((col: any) => {
          if (col.components) getAllFormioFields(col.components, fields);
        });
      }
    });
    return fields;
  }

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setTimeout(() => {
      const fields = getAllFormioFields(schema.components || []);
      const list = fields.map((field) => {
        let value = submission[field.key];

        if (value === undefined || value === null || value === "") {
          value = "(sin respuesta)";
        } else {
          if (field.type === "select") {
            if (typeof value === "object") {
              if (Array.isArray(value)) {
                value = value
                  .map((v) => (v.label ? v.label : v.value ?? v))
                  .join(", ");
              } else {
                value = value.label ?? value.value ?? JSON.stringify(value);
              }
            }
          }
          if (field.type === "datetime") {
            try {
              const dateObj = new Date(value);
              value = dateObj.toLocaleDateString("es-CO", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              });
            } catch {}
          }
          if (field.type === "checkbox" || field.type === "radio") {
            value = value === true || value === "true" ? "Sí" : "No";
          }
        }

        return { label: stripHtmlTags(field.label), value };
      });

      setSummaryList(list);
      setLoading(false);
    }, 100); // Simula carga pequeña (para UI fluida)
  }, [open, schema, submission]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 1200);
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      fullScreen
    >
      <DialogTitle
        sx={{
          position: "sticky",
          top: 0,
          background: "white",
          zIndex: 10,
          fontWeight: 700,
          pr: 6,
          borderBottom: "1px solid #eee",
        }}
      >
        Resumen del Formulario
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 16, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: "60vh" }}
          >
            <CircularProgress color="primary" />
            <Typography sx={{ mt: 2, color: "text.secondary" }}>
              Cargando formulario...
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {summaryList.map((item) => (
              <Stack
                key={item.label}
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1.5}
              >
                <Typography
                  sx={{
                    minWidth: { xs: "100%", sm: 260 },
                    fontWeight: 600,
                    wordBreak: "break-word",
                  }}
                >
                  {item.label}:
                </Typography>

                <TextField
                  value={item.value}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{
                    bgcolor: "#fafafa",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#ddd" },
                      "&:hover fieldset": { borderColor: "#bbb" },
                    },
                  }}
                />

                <Button
                  variant={
                    copiedField === item.label ? "contained" : "outlined"
                  }
                  color={copiedField === item.label ? "success" : "secondary"}
                  size="small"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={() => handleCopy(item.value, item.label)}
                  sx={{ textTransform: "none", minWidth: 90 }}
                >
                  {copiedField === item.label ? "¡Copiado!" : "Copiar"}
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
